<template>
  <el-dialog
    v-model="dialogVisible"
    title="用户信息"
    width="600px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    class="warm-theme-dialog"
  >
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="userProfile" class="profile-content">
      <div class="profile-header">
        <el-avatar :size="100" :src="userProfile.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" class="profile-avatar" />
        <div class="user-basic-info">
          <h2 class="username">{{ userProfile.name || userProfile.username || '未知用户' }}</h2>
          <p class="user-signature">{{ userProfile.signature || '暂无签名' }}</p>
        </div>
        <div class="profile-actions-header">
          <el-button 
            v-if="currentUser.id !== userProfile.id && userProfile.isFolloweed === 0 && userProfile.isFollowerd === 0" 
            type="primary" 
            size="small"
            @click="handleFollow"
          >
            关注
          </el-button>
          <el-button 
            v-else-if="currentUser.id !== userProfile.id && userProfile.isFolloweed === 0 && userProfile.isFollowerd === 1" 
            type="danger" 
            size="small"
            @click="handleFollow"
          >
            回关
          </el-button>
          <el-button 
            v-else-if="currentUser.id !== userProfile.id && userProfile.isFolloweed === 1 && userProfile.isFollowerd === 0" 
            type="warning" 
            size="small"
            @click="handleFollow"
          >
            已关注
          </el-button>
          <el-button 
            v-else-if="currentUser.id !== userProfile.id && userProfile.isFolloweed === 1 && userProfile.isFollowerd === 1" 
            type="success" 
            size="small"
            @click="handleFollow"
          >
            互相关注
          </el-button>
          <el-button 
            type="danger" 
            size="small"
            @click="openReportDialog"
            :disabled="currentUser.id === userProfile.id"
          >
            举报用户
          </el-button>
        </div>
      </div>

      <div class="profile-details">
        <div class="detail-section">
          <h3 class="section-title">账户信息</h3>
          <div class="detail-item">
            <label>用户账号:</label>
            <span>{{ userProfile.username || '未设置' }}</span>
          </div>
          <div class="detail-item">
            <label>用户邮箱:</label>
            <span>{{ userProfile.email || '未填写' }}</span>
          </div>
          <div class="detail-item">
            <label>联系电话:</label>
            <span>{{ userProfile.phone || '未填写' }}</span>
          </div>
          <div class="detail-item">
            <label>注册时间:</label>
            <span>{{ formatDate(userProfile.createTime) }}</span>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">统计信息</h3>
          <div class="stats-grid">
            <div class="stat-item" @click="showFollowings">
              <div class="stat-number">{{ userProfile.followeeCount || 0 }}</div>
              <div class="stat-label">关注数</div>
            </div>
            <div class="stat-item" @click="showFollowers">
              <div class="stat-number">{{ userProfile.followerCount || 0 }}</div>
              <div class="stat-label">粉丝数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ userProfile.postCount || 0 }}</div>
              <div class="stat-label">作品数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ userProfile.likeCount || 0 }}</div>
              <div class="stat-label">获赞数</div>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="currentUser.id !== userProfile.id">
          <h3 class="section-title">关系状态</h3>
          <div class="detail-item">
            <label>关注状态:</label>
            <span :class="followStatusClass">{{ followStatusText }}</span>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">账户状态</h3>
          <div class="detail-item">
            <label>用户角色:</label>
            <span>{{ userProfile.roleName || '普通用户' }}</span>
          </div>
          <div class="detail-item">
            <label>账户状态:</label>
            <span :class="statusClass">{{ statusText }}</span>
          </div>
        </div>
      </div>

    </div>
    <div v-else class="error">无法加载用户信息</div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { ElDialog, ElAvatar, ElButton, ElMessage } from 'element-plus';
import { getUserById, followUser } from '@/user/api/userApi';  // 导入关注API
import { useStore } from 'vuex';
import { formatDate } from '@/utils/date';
import ReportModal from '../report/ReportModal.vue';

// --- API响应接口定义 ---
interface OtherUserProfile {
  id: number;
  username: string;
  name: string;
  avatar: string;
  email?: string;
  phone?: string;
  signature?: string;
  status: number;
  roleName: string;
  createTime: string;
  isFolloweed: number;  // 是否关注了该用户 0否1是
  isFollowerd: number;  // 该用户是否关注了我 0否1是
  likeCount: number;
  postCount: number;
  followerCount: number;
  followeeCount: number;
}

interface ApiResponse {
  code: number;
  msg: string | null;
  data: OtherUserProfile;
}

interface Props {
  modelValue: boolean;
  userId: number;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
  (e: 'report-user', payload: { targetType: number, targetId: number }): void;
  (e: 'follow-status-change'): void; // 关注状态改变时触发
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const store = useStore();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const userProfile = ref<OtherUserProfile | null>(null);
const loading = ref(false);
const statusText = ref('');
const statusClass = ref('');
const reportModalRef = ref(); // 添加举报模态框引用

// 获取当前登录用户信息
const currentUser = computed(() => {
  return store.state.userInfo || { id: null };
});

// 关注状态相关的计算属性
const followStatusText = computed(() => {
  if (!userProfile.value) return '';
  if (userProfile.value.isFolloweed === 1 && userProfile.value.isFollowerd === 1) {
    return '互相关注';
  } else if (userProfile.value.isFolloweed === 0 && userProfile.value.isFollowerd === 1) {
    return '对方已关注你';
  } else if (userProfile.value.isFolloweed === 1 && userProfile.value.isFollowerd === 0) {
    return '已关注';
  } else {
    return '未关注';
  }
});

const followStatusClass = computed(() => {
  if (!userProfile.value) return '';
  if (userProfile.value.isFolloweed === 1 && userProfile.value.isFollowerd === 1) {
    return 'status-mutual';
  } else if (userProfile.value.isFolloweed === 0 && userProfile.value.isFollowerd === 1) {
    return 'status-follower';
  } else if (userProfile.value.isFolloweed === 1) {
    return 'status-followed';
  } else {
    return 'status-not-followed';
  }
});

// 更新状态相关信息
const updateStatusInfo = (status: number) => {
  switch (status) {
    case 0:
      statusText.value = '封禁';
      statusClass.value = 'status-banned';
      break;
    case 1:
      statusText.value = '正常';
      statusClass.value = 'status-normal';
      break;
    case 2:
      statusText.value = '禁言';
      statusClass.value = 'status-muted';
      break;
    default:
      statusText.value = '未知';
      statusClass.value = 'status-unknown';
  }
};

const loadUserProfile = async () => {
  if (!props.userId) return;

  try {
    loading.value = true;
    
    // 调用API获取用户资料
    const response = await getUserById(props.userId);
    
    console.log('API Response:', response); // 添加调试日志
    
    // 检查响应结构并提取数据
    let userData = null;
    
    // 情况1: 直接返回用户数据
    if (response.id) {
      userData = response;
    } 
    // 情况2: 包含标准响应格式 { code: 1, msg: null, data: {...} }
    else if (response.data && response.data.code === 1 && response.data.data) {
      userData = response.data.data;
    }
    // 情况3: 兼容其他可能的格式
    else if (response.data && typeof response.data === 'object' && response.data.id) {
      userData = response.data;
    }
    
    if (userData) {
      userProfile.value = userData;
      // 更新状态信息
      updateStatusInfo(userData.status);
    } else {
      console.error('API返回的数据格式不正确:', response);
      throw new Error('获取用户资料失败：数据格式不正确');
    }
  } catch (error: any) {
    console.error('获取用户资料失败:', error);
    userProfile.value = null;
    ElMessage.error('获取用户资料失败');
  } finally {
    loading.value = false;
  }
};

// 关注/取消关注用户
const handleFollow = async () => {
  if (!userProfile.value) return;
  
  if (currentUser.value.id === userProfile.value.id) {
    ElMessage.warning('不能关注自己');
    return;
  }

  try {
    // 直接调用关注API，无需确认
    const response = await followUser(userProfile.value.id);
    
    if (response.code === 1) {
      // 保存原来的关注状态
      const wasFollowed = userProfile.value.isFolloweed === 1;
      
      // 更新关注状态
      userProfile.value.isFolloweed = userProfile.value.isFolloweed === 1 ? 0 : 1;
      
      // 更新统计数
      if (userProfile.value.isFolloweed === 1) {
        
        // 如果对方原本关注了当前用户，粉丝数也要+1
        if (userProfile.value.isFollowerd === 1) {
          userProfile.value.followerCount = (userProfile.value.followerCount || 0) + 1;
        }
        
        ElMessage.success(`已成功关注 ${userProfile.value.name || userProfile.value.username}`);
      } else {
        
        // 如果对方之前关注了当前用户，粉丝数也要-1
        if (userProfile.value.isFollowerd === 1 && wasFollowed) {
          userProfile.value.followerCount = Math.max(0, (userProfile.value.followerCount || 0) - 1);
        }
        
        ElMessage.success(`已取消关注 ${userProfile.value.name || userProfile.value.username}`);
      }
      
      // 触发关注状态改变事件，通知父组件更新
      emit('follow-status-change', {
        userId: userProfile.value.id,
        isFollowed: userProfile.value.isFolloweed === 1,
        followeeCount: userProfile.value.followeeCount,
        followerCount: userProfile.value.followerCount
      });
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('关注操作失败:', error);
    ElMessage.error('操作失败，请稍后再试');
  }
};

// 显示关注列表
const showFollowings = () => {
  if (!userProfile.value) return;
  // 触发事件让父组件显示关注列表
  emit('show-followings', userProfile.value.id);
};

// 显示粉丝列表
const showFollowers = () => {
  if (!userProfile.value) return;
  // 触发事件让父组件显示粉丝列表
  emit('show-followers', userProfile.value.id);
};

// 关闭对话框
const handleClose = () => {
  emit('close');
};

// 举报用户
const openReportDialog = () => {
  if (!userProfile.value) return;
  
  // 检查是否为自己，如果是则不允许举报
  if (currentUser.value.id === userProfile.value.id) {
    ElMessage.warning('不能举报自己');
    return;
  }
  
  // 通过emit向父组件传递举报事件
  emit('report-user', {
    targetType: 3, // 3 表示用户
    targetId: userProfile.value.id
  });
};

// 监听弹窗显示状态变化
watch([dialogVisible, () => props.userId], ([newDialogVisible, newUserId], [oldDialogVisible, oldUserId]) => {
  // 当弹窗变为可见或userId发生变化时加载用户资料
  if (newDialogVisible && (newUserId !== oldUserId || !userProfile.value || userProfile.value.id !== newUserId)) {
    loadUserProfile();
  }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@400;500;600;700&display=swap');

.warm-theme-dialog {
  --bg-primary: #f9f5f0;
  --bg-secondary: #f0e6db;
  --accent-color: #d48341;
  --text-main: #5c4329;
  --text-light: #8b7d6b;
  --card-bg: #fffaf0;
  --border-radius: 12px;
  --shadow: 0 4px 12px rgba(139, 125, 107, 0.15);
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

/* 添加编织纹理背景 */
.warm-theme-dialog :deep(.el-dialog__body) {
  background-image: 
    radial-gradient(circle at 15px 15px, #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at calc(100% - 15px) 15px, #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at 15px calc(100% - 15px), #e6d5c3 2px, transparent 3px),
    radial-gradient(circle at calc(100% - 15px) calc(100% - 15px), #e6d5c3 2px, transparent 3px);
  background-size: 60px 60px;
  background-position: 0 0;
  padding: 20px;
}

.profile-container {
  padding: 20px;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
  min-height: 100%;
}

.loading {
  text-align: center;
  padding: 40px;
  color: var(--text-light);
  font-family: 'Quicksand', sans-serif;
}

.profile-content {
  background: var(--card-bg);
  border-radius: var(--border-radius);
  padding: 20px;
  box-shadow: var(--shadow);
  position: relative;
  /* 添加钩针花边装饰 */
  background-image: 
    linear-gradient(90deg, transparent 45%, rgba(212, 131, 65, 0.1) 50%, transparent 55%),
    linear-gradient(180deg, transparent 45%, rgba(212, 131, 65, 0.1) 50%, transparent 55%);
  background-size: 40px 40px;
}

.detail-section {
  margin-bottom: 25px;
  padding: 15px;
  background: 
    linear-gradient(135deg, #fefcf7 0%, #fffaf0 100%),
    radial-gradient(at top left, rgba(212, 131, 65, 0.1) 0%, transparent 50%),
    radial-gradient(at bottom right, rgba(212, 131, 65, 0.1) 0%, transparent 50%);
  border-radius: var(--border-radius);
  border-left: 3px solid var(--accent-color);
  position: relative;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.15);
  border-radius: 15px;
}

.detail-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid rgba(212, 131, 65, 0.15);
  border-radius: var(--border-radius);
  pointer-events: none;
}

.section-title {
  margin: 0 0 15px 0;
  padding-bottom: 8px;
  color: var(--text-main);
  font-size: 18px;
  font-weight: 600;
  font-family: 'Quicksand', 'Nunito', sans-serif;
  position: relative;
}

.section-title::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30px;
  height: 2px;
  background: linear-gradient(to right, var(--accent-color), transparent);
}

.detail-item {
  display: flex;
  margin-bottom: 12px;
}

.detail-item label {
  width: 100px;
  color: var(--text-light);
  font-weight: 500;
  font-family: 'Nunito', sans-serif;
}

.detail-item span {
  flex: 1;
  color: var(--text-main);
  font-family: 'Open Sans', sans-serif;
}

.stat-item {
  text-align: center;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: 
    linear-gradient(135deg, #fefcf7 0%, #fffaf0 100%),
    radial-gradient(at top left, rgba(212, 131, 65, 0.1) 0%, transparent 50%),
    radial-gradient(at bottom right, rgba(212, 131, 65, 0.1) 0%, transparent 50%);
  border: 1px solid rgba(212, 131, 65, 0.15);
  position: relative;
  overflow: hidden;
}

.stat-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-color), transparent);
}

.stat-item:hover {
  background: 
    linear-gradient(135deg, #ffffff 0%, #fffaf0 100%),
    radial-gradient(at top left, rgba(212, 131, 65, 0.1) 0%, transparent 50%),
    radial-gradient(at bottom right, rgba(212, 131, 65, 0.1) 0%, transparent 50%);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(139, 125, 107, 0.15);
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: var(--accent-color);
  font-family: 'Quicksand', 'Nunito', sans-serif;
  position: relative;
}

.stat-number::before {
  content: "";
  position: absolute;
  top: -5px;
  left: -5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(212, 131, 65, 0.1);
  z-index: -1;
}

.stat-label {
  font-size: 14px;
  color: var(--text-light);
  margin-top: 5px;
  font-family: 'Open Sans', sans-serif;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  position: relative;
}

.profile-avatar {
  border: 3px solid var(--card-bg);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.profile-avatar::before {
  content: "";
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border: 2px solid var(--accent-color);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.3;
}

.user-basic-info {
  flex: 1;
}

.user-basic-info h2 {
  margin: 0 0 10px 0;
  font-size: 24px;
  color: var(--text-main);
  font-family: 'Quicksand', 'Nunito', sans-serif;
  position: relative;
}

.user-basic-info h2::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 50px;
  height: 2px;
  background: var(--accent-color);
}

.user-signature {
  margin: 0;
  color: var(--text-light);
  font-style: italic;
  font-family: 'Open Sans', sans-serif;
}

.profile-actions-header {
  display: flex;
  align-items: center;
  height: 100px; /* 匹配头像高度 */
  justify-content: flex-end;
  margin-left: auto; /* 推到右侧 */
  z-index: 10;
}

.profile-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed var(--bg-secondary);
  position: relative;
  z-index: 2; /* 确保按钮在顶层 */
}

.warm-button {
  background-color: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
  border-radius: 20px;
  padding: 8px 20px;
  transition: all 0.3s ease;
  font-family: 'Nunito', sans-serif;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  z-index: 1;
  min-width: 80px; /* 确保按钮始终占用空间 */
}

.warm-button::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
}

.warm-button:hover::after {
  opacity: 1;
}

.warm-button:hover {
  background-color: #c07435;
  border-color: #c07435;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(212, 131, 65, 0.3);
}

.error {
  text-align: center;
  padding: 40px;
  color: #F56C6C;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
}

/* 新增状态样式 */
.status-mutual {
  color: #67c23a;
  font-weight: bold;
}

.status-followed {
  color: #409eff;
}

.status-follower {
  color: #e6a23c;
}

.status-not-followed {
  color: var(--text-light);
}

.status-banned {
  color: #f56c6c;
}

.status-normal {
  color: #67c23a;
}

.status-muted {
  color: #e6a23c;
}

.status-unknown {
  color: var(--text-light);
}
</style>